import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isToolboxVisible } from '../../../toolbox/functions.web';
import { MOBILE_SCREENSHARE_FLOAT_BOTTOM_GAP, MOBILE_SCREENSHARE_FLOAT_RIGHT_GAP, MOBILE_SCREENSHARE_ROTATE_BUTTON_SIZE, TOOLBAR_FULL_WIDTH_HEIGHT } from '../../constants';
import logger from '../../logger';
import { isMobileScreenshareLandscapeActive, toggleMobileScreenshareLandscape } from '../../orientation.web';
const useStyles = makeStyles()(theme => {
    return {
        button: {
            alignItems: 'center',
            backgroundColor: 'rgba(28, 28, 30, 0.82)',
            border: 'none',
            borderRadius: '50%',
            bottom: TOOLBAR_FULL_WIDTH_HEIGHT + MOBILE_SCREENSHARE_FLOAT_BOTTOM_GAP,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.35)',
            color: theme.palette.text01,
            cursor: 'pointer',
            display: 'flex',
            height: MOBILE_SCREENSHARE_ROTATE_BUTTON_SIZE,
            justifyContent: 'center',
            padding: 0,
            pointerEvents: 'auto',
            position: 'absolute',
            right: MOBILE_SCREENSHARE_FLOAT_RIGHT_GAP,
            width: MOBILE_SCREENSHARE_ROTATE_BUTTON_SIZE,
            zIndex: 2,
            '&:active': {
                opacity: 0.85
            }
        },
        icon: {
            display: 'block',
            fill: 'currentColor',
            height: 22,
            width: 22
        }
    };
});
/**
 * 手机共享聚焦布局：右下角竖屏/横屏切换按钮。
 *
 * @returns {ReactElement}
 */
function MobileScreenshareRotateButton() {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const toolboxVisible = useSelector(isToolboxVisible);
    const [landscape, setLandscape] = useState(isMobileScreenshareLandscapeActive);
    /**
     * 阻断 touch/pointer 冒泡到 #videospace，避免点旋转钮时连带 toggle 上下栏。
     * 手机端先触发 touchstart，仅在 onClick 里 stopPropagation 拦不住。
     */
    const stopChromeToggleBubble = useCallback((event) => {
        event.stopPropagation();
    }, []);
    const onClick = useCallback(async (event) => {
        event.stopPropagation();
        logger.info(`[手机共享布局] 用户点击横竖屏切换 currentLandscape=${landscape}`);
        try {
            const nextLandscape = await toggleMobileScreenshareLandscape();
            setLandscape(nextLandscape);
            logger.info(`[手机共享布局] 横竖屏切换完成 landscape=${nextLandscape}`);
        }
        catch (error) {
            logger.error('[手机共享布局] 横竖屏切换异常', error);
        }
    }, [landscape]);
    const label = landscape
        ? t('meetingUi.mobileScreenshare.rotateToPortrait')
        : t('meetingUi.mobileScreenshare.rotateToLandscape');
    // 上下栏隐藏时一并隐藏旋转按钮，避免挡共享画面；再次点画面恢复 chrome 后显示。
    if (!toolboxVisible) {
        return null;
    }
    return (React.createElement("button", { "aria-label": label, "aria-pressed": landscape, className: classes.button, "data-prevent-toolbox-toggle": 'true', onClick: onClick, onPointerDown: stopChromeToggleBubble, onTouchEnd: stopChromeToggleBubble, onTouchStart: stopChromeToggleBubble, type: 'button' },
        React.createElement("svg", { "aria-hidden": true, className: classes.icon, viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
            React.createElement("path", { d: 'M9 3.75A1.75 1.75 0 0 1 10.75 2h2.5A1.75 1.75 0 0 1 15 3.75v10.5A1.75 1.75 0 0 1'
                    + ' 13.25 16h-2.5A1.75 1.75 0 0 1 9 14.25V3.75Zm1.75-.25a.25.25 0 0 0-.25.25v10.5c0'
                    + ' .138.112.25.25.25h2.5a.25.25 0 0 0 .25-.25V3.75a.25.25 0 0 0-.25-.25h-2.5Z', fill: 'currentColor' }),
            React.createElement("path", { d: 'M7.03 17.03a.75.75 0 0 1 0-1.06l1.72-1.72H5.75a.75.75 0 0 1 0-1.5h5a.75.75 0 0 1'
                    + ' .75.75v5a.75.75 0 0 1-1.5 0v-2.99l-1.72 1.72a.75.75 0 0 1-1.06 0Z', fill: 'currentColor' }),
            React.createElement("path", { d: 'M16.97 6.97a.75.75 0 0 1 0 1.06l-1.72 1.72h3a.75.75 0 0 1 0 1.5h-5a.75.75 0 0 1'
                    + ' -.75-.75v-5a.75.75 0 0 1 1.5 0v2.99l1.72-1.72a.75.75 0 0 1 1.06 0Z', fill: 'currentColor' }))));
}
export default MobileScreenshareRotateButton;
